import { FC, useEffect, useRef, useState, CSSProperties } from 'react';
import { Table as ReactTable, flexRender } from '@tanstack/react-table';
import styled from 'styled-components';
import tw from 'twin.macro';
import { TableSize } from '../enums';
import { useProjectContext } from '@/context/ProjectContext';
import {
  defaultSoldCoOperative,
  defaultSoldLittleHouse,
  defaultSoldTableAdInfoColumns,
  defaultSoldTableRealEstateColumns,
  defaultSoldTableSaleInfoColumns,
} from '@/context/SoldDefault';
import { defaultAdsTableColumns } from '@/context/AdsDefault';
import { ColumnType } from '@/types/dataTypes';
import { ColumnChoiceType } from '@/enums';
import { getSelectedColumnOptions, SortingType } from './table/helpers';
const Container = styled.div<{ $size?: TableSize }>`
  ${({ $size }) =>
      $size === TableSize.small
          ? tw`min-h-full p-2 pt-0 overflow-hidden w-full`
          : tw`pt-0 px-2 overflow-x-auto`};

  /* Fill exactly the space left under the table toolbar so the inner scroll
     container's bottom edge matches the visible bottom — otherwise the sticky
     summary footer pins to an off-screen edge (visible when the toolbar wraps
     at narrow window widths). */
  ${({ $size }) =>
      $size !== TableSize.small &&
      `flex: 1; min-height: 0; display: flex; flex-direction: column;`};
`;

const StyledTable = styled.table`
  ${tw`text-left leading-none w-full`};
  table-layout: auto;
  min-width: 100%;
`;

const TableHeaderRow = styled.tr`
  ${tw`transition-colors`};
`;

const TableRow = styled.tr<{ $isSelected?: boolean }>`
  &:nth-child(even) {
    background-color: ${({ $isSelected }) =>
        $isSelected ? '#6b9473' : '#c5dbce'};
  }

  background-color: ${({ $isSelected }) =>
      $isSelected ? '#93b59e' : '#F3F7F4'};

  &:hover {
    background-color: #e7ece9;
    &:nth-child(even) {
      background-color: #bbc7bf;
    }
  }
`;

const Th = styled.th<{ $size?: TableSize; $isNarrow?: boolean; $isDraggable?: boolean; $isDragging?: boolean; $isDragOver?: boolean }>`
  ${tw`relative font-normal text-slate-800 cursor-pointer`};
  ${({ $isDragging }) => $isDragging && 'opacity: 0.4;'};
  ${({ $isDragOver }) => $isDragOver && 'box-shadow: inset 2px 0 0 0 #6b9473;'};
  ${({ $size }) =>
      $size === TableSize.small
          ? tw`pr-[0.9rem] pb-[0.65rem]`
          : tw`p-2 md:py-0.5 md:px-[0.5rem] lg:pb-3 lg:pt-2`};

  font-size: ${({ $size }) =>
      $size === TableSize.small ? '0.55rem' : '0.85rem'};
  position: sticky;
  top: 0;
  background-color: #F3F7F4;
  z-index: 10;
  width: auto;
  min-width: 0;
`;

const Td = styled.td<{ $size?: TableSize }>`
  ${tw`p-2`};
  color: black;
  font-size: ${({ $size }) => $size === TableSize.small && '0.55rem'};
  width: auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tbody = styled.tbody`
  ${tw`text-slate-500 text-xs font-light`};
`;

const TFoot = styled.tfoot`
  ${tw`text-slate-800 text-xs font-bold border-t border-dashed border-slate-300/50`};
  position: sticky;
  bottom: -20px;
  background-color: #F3F7F4;
  z-index: 10;
`;

const BottomScrollbar = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0;
  right: 0;
  width: 100%;
  height: 10px;
  overflow-x: scroll;
  overflow-y: hidden;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  z-index: 20;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #4a5568;
    border-radius: 6px;
    border: 2px solid #e2e8f0;
    cursor: pointer;
  }
  &::-webkit-scrollbar-track {
    background-color: #f7fafc;
    border-radius: 6px;
  }
`;

const BottomScrollbarContent = styled.div`
  height: 1px;
  pointer-events: none;
`;

const RightScrollbar = styled.div`
  position: absolute;
  top: 45px;
  right: -2px;
  width: 10px;
  height: calc(100% - 53px);
  overflow-y: scroll;
  overflow-x: hidden;
  background: #f8f9fa;
  border-left: 1px solid #e9ecef;
  z-index: 20;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #4a5568;
    border-radius: 6px;
    border: 2px solid #e2e8f0;
    cursor: pointer;
  }
  &::-webkit-scrollbar-track {
    background-color: #f7fafc;
    border-radius: 6px;
  }
`;

const RightScrollbarContent = styled.div`
  width: 1px;
  pointer-events: none;
`;

const TableContainer = styled.div`
  overflow: scroll;

  overflow-x: auto;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-bottom: 20px; /* Clearance so the last row can scroll past the sticky summary footer */
  width: 100%;
  max-width: 100%;

  /* Hide native scrollbars */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */

  &::-webkit-scrollbar {
    display: none; /* Webkit browsers */
  }
`;

interface Props {
  table: ReactTable<any>;
  rowSelection: { [key: string]: boolean };
  size?: TableSize;
  hideFooter: boolean;
  className?: string;
  children: React.ReactNode;
  sorting: SortingType | null;
  pinSelectColumn?: boolean;
}

const Table: FC<Props> = ({
                            table,
                            rowSelection,
                            size,
                            className,
                            hideFooter,
                            children,
                            sorting,
                            pinSelectColumn
                          }) => {
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const bottomScrollContentRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollContentRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<{ top: number; left: number }>({ top: 0, left: 0 });

  // Columns that must stay fixed and cannot be reordered via drag.
  const NON_REORDERABLE = ['index', 'select', 'hideRow'];
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

  const reorderColumn = (draggedId: string, targetId: string) => {
    if (draggedId === targetId) return;
    // Display order of all currently rendered columns (respects columnOrder/pinning).
    const current = table.getVisibleLeafColumns().map((c) => c.id);
    const from = current.indexOf(draggedId);
    const to = current.indexOf(targetId);
    if (from < 0 || to < 0) return;
    current.splice(to, 0, current.splice(from, 1)[0]);
    // Never store hideRow in the order: columns missing from columnOrder are
    // appended last in definition order, which keeps hideRow rightmost even
    // when a column preset later adds columns that aren't in the saved order.
    table.setColumnOrder(current.filter((id) => id !== 'hideRow'));
  };

  const {
    project: {
      own_choice_reference_fields,
    },
    columnOption,
  } = useProjectContext();

  // Pin a column to the left edge. `elevated` is used for the header/footer
  // rows so the pinned cell carries the header background and sits above the
  // horizontally-scrolling cells it needs to cover (column names / summary).
  const getCommonPinningStyles = (left: number, elevated = false): CSSProperties => {
    return {
      left: left + "px",
      position: 'sticky',
      zIndex: elevated ? 11 : 1,
      backgroundColor: elevated ? '#F3F7F4' : 'inherit',
      boxShadow: left > 0 ? '-4px 0 4px -4px black inset' : undefined,
    }
  }

  // Keep the rightmost "Dölj" (hideRow) column pinned to the right edge so it
  // stays visible while the table scrolls horizontally, mirroring the pinned
  // index/select columns on the left. `withTop` keeps the header cell pinned
  // vertically too.
  const getRightPinningStyles = (elevated: boolean, withTop = false): CSSProperties => {
    return {
      right: 0,
      position: 'sticky',
      top: withTop ? 0 : undefined,
      zIndex: elevated ? 11 : 1,
      backgroundColor: elevated ? '#F3F7F4' : 'inherit',
      boxShadow: '4px 0 4px -4px black inset',
    }
  }

  useEffect(() => {
    const sortingId = sorting ? sorting.id : null;
    let columnChoice: ColumnType[] = defaultAdsTableColumns;
    if (columnOption === ColumnChoiceType.sold)
      columnChoice = defaultSoldTableSaleInfoColumns;
    else if (columnOption === ColumnChoiceType.ads)
      columnChoice = defaultSoldTableAdInfoColumns;
    else if (columnOption === ColumnChoiceType.realEstate)
      columnChoice = defaultSoldTableRealEstateColumns;
    else if (columnOption === ColumnChoiceType.coOperative)
      columnChoice = defaultSoldCoOperative;
    else if (columnOption === ColumnChoiceType.littleHouse)
      columnChoice = defaultSoldLittleHouse;
    else if (columnOption === ColumnChoiceType.ownChoice) {
      columnChoice = getSelectedColumnOptions();
    }
    const ids = columnChoice.filter((row) => row.isVisible).map((row) => row.id);
    if (sorting && ids.includes(sortingId) && table.getAllColumns().map((item => item.id)).includes(sortingId)) {
      table.setSorting([sorting]);
    }
  }, [sorting]);

  // Preserve scroll position when row selection changes
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    // Capture scroll position on mousedown (before state update)
    const handleMouseDown = (e: MouseEvent) => {
      // Check if the click is on a checkbox or its container
      const target = e.target as HTMLElement;
      const isCheckboxClick = target.closest('input[type="checkbox"]') || target.closest('[role="checkbox"]');

      if (isCheckboxClick) {
        scrollPositionRef.current = {
          top: tableContainer.scrollTop,
          left: tableContainer.scrollLeft
        };
      }
    };

    tableContainer.addEventListener('mousedown', handleMouseDown, true);

    return () => {
      tableContainer.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, []);

  // Restore scroll position after row selection changes
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) return;

    // Restore scroll position after render
    const { top, left } = scrollPositionRef.current;
    if (top !== 0 || left !== 0) {
      tableContainer.scrollTop = top;
      tableContainer.scrollLeft = left;
    }
  }, [rowSelection]);

  // Sync bottom and right scrollbars with table scroll position
  useEffect(() => {
    const bottomScroll = bottomScrollRef.current;
    const rightScroll = rightScrollRef.current;
    const tableContainer = tableContainerRef.current;
    const bottomScrollContent = bottomScrollContentRef.current;
    const rightScrollContent = rightScrollContentRef.current;

    if (!bottomScroll || !rightScroll || !tableContainer || !bottomScrollContent || !rightScrollContent) return;

    // Update scrollbar dimensions to match table content
    const updateScrollbarDimensions = () => {
      // Use the actual table width for accurate scrollbar sizing
      const table = tableContainer.querySelector('table');
      if (table) {
        // Add a larger buffer to ensure we can scroll to the very end
        bottomScrollContent.style.width = `${table.scrollWidth + 20}px`;
      } else {
        bottomScrollContent.style.width = `${tableContainer.scrollWidth + 20}px`;
      }
      rightScrollContent.style.height = `${tableContainer.scrollHeight}px`;
    };

    // Sync horizontal scroll positions
    const syncBottomToTable = () => {
      tableContainer.scrollLeft = bottomScroll.scrollLeft;
    };

    const syncTableToBottom = () => {
      bottomScroll.scrollLeft = tableContainer.scrollLeft;
    };

    // Sync vertical scroll positions
    const syncRightToTable = () => {
      tableContainer.scrollTop = rightScroll.scrollTop;
    };

    const syncTableToRight = () => {
      rightScroll.scrollTop = tableContainer.scrollTop;
    };

    updateScrollbarDimensions();

    // Add horizontal scroll listeners
    bottomScroll.addEventListener('scroll', syncBottomToTable);
    tableContainer.addEventListener('scroll', syncTableToBottom);

    // Add vertical scroll listeners
    rightScroll.addEventListener('scroll', syncRightToTable);
    tableContainer.addEventListener('scroll', syncTableToRight);

    // Update dimensions on table changes
    const observer = new ResizeObserver(updateScrollbarDimensions);
    observer.observe(tableContainer);

    // Also observe the table element itself for more accurate sizing
    const table = tableContainer.querySelector('table');
    if (table) {
      observer.observe(table);
    }

    return () => {
      bottomScroll.removeEventListener('scroll', syncBottomToTable);
      tableContainer.removeEventListener('scroll', syncTableToBottom);
      rightScroll.removeEventListener('scroll', syncRightToTable);
      tableContainer.removeEventListener('scroll', syncTableToRight);
      observer.disconnect();
    };
  }, [table.getRowModel().rows.length, table.getAllColumns().length]);

  function uuidv4() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
        (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
  }
  return (
      <Container $size={size} className={className}>
        <TableContainer ref={tableContainerRef} className="scrollbar">
          <StyledTable>
            <thead>
            {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, headerIndex) => {
                    const columnId = header.column.id;
                    const isDraggable = !NON_REORDERABLE.includes(columnId);
                    return (
                      <Th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={pinSelectColumn ? (columnId === 'index' || columnId === 'select') ? getCommonPinningStyles(columnId === 'select' ? 25 : 0, true) : columnId === 'hideRow' ? getRightPinningStyles(true, true) : undefined : undefined}
                          $size={size}
                          $isNarrow={headerIndex === 0}
                          $isDraggable={isDraggable}
                          $isDragging={draggedColumnId === columnId}
                          $isDragOver={dragOverColumnId === columnId && draggedColumnId !== columnId}
                          draggable={isDraggable}
                          onDragStart={isDraggable ? () => setDraggedColumnId(columnId) : undefined}
                          onDragOver={isDraggable ? (e) => {
                            e.preventDefault();
                            if (dragOverColumnId !== columnId) setDragOverColumnId(columnId);
                          } : undefined}
                          onDrop={isDraggable ? (e) => {
                            e.preventDefault();
                            if (draggedColumnId) reorderColumn(draggedColumnId, columnId);
                            setDraggedColumnId(null);
                            setDragOverColumnId(null);
                          } : undefined}
                          onDragEnd={() => {
                            setDraggedColumnId(null);
                            setDragOverColumnId(null);
                          }}>
                        {header.isPlaceholder ? null : (
                            <>
                              {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                              )}
                            </>
                        )}
                      </Th>
                    );
                  })}
                </TableHeaderRow>
            ))}
            </thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.original.id} $isSelected={rowSelection[row.original.id]}>
                    {row.getVisibleCells().map((cell, cellIndex) => (
                        <Td key={uuidv4()} $size={size} $isNarrow={cellIndex === 0} style={pinSelectColumn ? (cell.column.id === 'index' || cell.column.id === 'select') ? getCommonPinningStyles(cellIndex == 1 ? 25 : 0) : cell.column.id === 'hideRow' ? getRightPinningStyles(false) : undefined : undefined}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Td>
                    ))}
                  </TableRow>
              ))}
            </Tbody>
            {hideFooter && (
                <TFoot>
                  {table.getFooterGroups().map((footerGroup) => (
                      <TableRow key={uuidv4()}>
                        {footerGroup.headers.map((footer) => {
                          const footerPin = pinSelectColumn
                            ? (footer.column.id === 'index' || footer.column.id === 'select')
                              ? getCommonPinningStyles(footer.column.id === 'select' ? 25 : 0, true)
                              : footer.column.id === 'hideRow'
                                ? getRightPinningStyles(true)
                                : undefined
                            : undefined;
                          return (
                              <Td key={uuidv4()} colSpan={footer.colSpan} $size={size} style={{ paddingBottom: '20px', ...footerPin }}>
                                {flexRender(
                                    footer.column.columnDef.footer,
                                    footer.getContext()
                                )}
                              </Td>
                          );
                        })}
                      </TableRow>
                  ))}
                </TFoot>
            )}
          </StyledTable>
        </TableContainer>
        <BottomScrollbar ref={bottomScrollRef}>
          <BottomScrollbarContent ref={bottomScrollContentRef} />
        </BottomScrollbar>
        <RightScrollbar ref={rightScrollRef}>
          <RightScrollbarContent ref={rightScrollContentRef} />
        </RightScrollbar>
        {size !== TableSize.small && size !== TableSize.medium && (
            children
        )}
      </Container>
  );
}

export default Table;